import clsx from 'clsx';
import { useId, useState } from 'react';
import { useLinearProjects } from '@/api/studio';

/**
 * Name of a Linear project: type it, or pick one that exists. A name that matches no project
 * offers «Создать новый проект»; the server finds by name first and creates otherwise.
 */
export function LinearProjectPicker({ value, onChange, placeholder, ariaLabel }: { value: string; onChange: (name: string) => void; placeholder?: string; ariaLabel?: string }) {
  const { data } = useLinearProjects();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  // Until the person types, the whole list shows: a prefilled name (the portal project's
  // title in settings) must not filter every Linear project away.
  const [filtering, setFiltering] = useState(false);
  const id = useId();
  const listId = `${id}-list`;

  const typed = value.trim();
  const needle = typed.toLowerCase();
  const all = data?.projects ?? [];
  const found = filtering && needle ? all.filter((p) => p.name.toLowerCase().includes(needle)) : all;
  const exact = all.some((p) => p.name.toLowerCase() === needle);
  const options: { key: string; label: string; name: string; create?: boolean }[] = [
    ...(typed && !exact ? [{ key: 'new', label: `Создать новый проект «${typed}»`, name: typed, create: true }] : []),
    ...found.map((p) => ({ key: p.id, label: p.name, name: p.name })),
  ];
  const showList = open && !!data?.connected && options.length > 0;

  const pick = (i: number) => {
    onChange(options[i].name);
    setOpen(false);
    setActive(-1);
  };

  return (
    <div className="relative">
      <input
        className="field"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && active >= 0 ? `${id}-${active}` : undefined}
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        onFocus={() => {
          setOpen(true);
          setFiltering(false);
        }}
        onBlur={() => {
          setOpen(false);
          setActive(-1);
        }}
        onChange={(e) => {
          onChange(e.target.value);
          setFiltering(true);
          setOpen(true);
          setActive(-1);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (showList) e.stopPropagation();
            setOpen(false);
            setActive(-1);
          } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (!options.length) return;
            e.preventDefault();
            setOpen(true);
            const step = e.key === 'ArrowDown' ? 1 : -1;
            setActive((a) => (a < 0 ? (step > 0 ? 0 : options.length - 1) : (a + step + options.length) % options.length));
          } else if (e.key === 'Enter' && showList && active >= 0) {
            // A chosen item must not also submit the form around the field.
            e.preventDefault();
            pick(active);
          }
        }}
      />
      {showList && (
        <ul id={listId} role="listbox" className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg bg-canvas py-1 text-sm shadow-pop">
          {options.map((o, i) => (
            <li
              key={o.key}
              id={`${id}-${i}`}
              role="option"
              aria-selected={i === active}
              // mousedown, not click: the input would lose focus and close the list first.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(i);
              }}
              onMouseEnter={() => setActive(i)}
              className={clsx('cursor-pointer px-3 py-1.5', i === active && 'bg-sunken', o.create && 'text-muted')}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
      {data && !data.connected && <p className="mt-1.5 text-xs text-muted">Linear не подключён — проект создастся без задач</p>}
    </div>
  );
}
